import PropTypes from "prop-types";
import { Grid, Box, Card, TextField, Autocomplete, Divider } from "@mui/material";

import { useSoftUIController } from "hooks/context";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftButton from "components/SoftComponents/SoftButton";

import theme from "assets/theme";

import { sidebarWrapper } from "utils/wrapper";
import HealthStatusCard from "components/Cards/HealthStatusCard";
import StatsCounterCard from "components/Cards/StatsCounterCard";
import DeviceStatsCard from "components/Cards/DeviceStatsCard";
import LiveView from "components/LiveView";
import CameraLatest from "components/Camera/CameraLatest";
// 1. Added Edit and Save icons
import { Close, Edit, Save, NotificationsOff, NotificationsActive, Cancel } from "@mui/icons-material";
import MapLinkButton from "components/Buttons/MapLinkButton";
import { useEffect, useState } from "react";
import { getConnectivityIcon, getDeviceTypeIcon } from "utils/device";
import { Link } from "react-router-dom";
import InspectionLiveviewCard from "modules/inspections/components/InspectionLiveviewCard";
import { getNamingConventions } from "utils/formatting";

function StopmonitorSidebar({ map, user, selectedDevice, setSelectedDevice, mapOptions, inspections, mapOption, setMapOption, isAttentionMode, setIsAttentionMode, isEditMode, setIsEditMode, handleSaveLocations }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, userPermissions } = controller;

    const canDeviceEdit = userPermissions.includes('devices:edit');
    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canAdvancedDevicesEdit = userPermissions.includes('devices-advanced:edit');

    const accessUserLocation = userPermissions.includes('misc:user-location');
    const canAssignmentsView = userPermissions.includes('operations');
    const showMapOptions = mapOptions.length > 0;

    const [header, subheader, triheader] = getNamingConventions(selectedDevice?.name || "");
    const [currentLocation, setCurrentLocation] = useState([0, 0]);

    useEffect(() => {
        if (accessUserLocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setCurrentLocation([latitude, longitude]);
                },
                (error) => {
                    console.error("Error detecting location:", error);
                    if (error.code === error.TIMEOUT) {
                        console.warn("Location request timed out. Setting to 0,0.");
                    }
                    setCurrentLocation([0, 0]);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 15000,
                    maximumAge: 0
                }
            );
        }
    }, [user, accessUserLocation]);

    useEffect(() => {
        const handleWheel = (e) => {
            if (e.ctrlKey) {
                e.preventDefault();
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, []);

    // 3. Handler for toggling edit mode and triggering the save
    const handleEditToggle = () => {
        if (isEditMode && handleSaveLocations) {
            handleSaveLocations(); // Trigger the save action in the parent
        }
        if (setIsEditMode) {
            setIsEditMode(!isEditMode);
        }
    };

    const handleEditCancel = () => {
        if (setIsEditMode) {
            setIsEditMode(!isEditMode);
        }
    };

    const handleAttentionToggle = () => {
        if (setIsAttentionMode) {
            setIsAttentionMode(prev => !prev);
            setMapOption(null);
            setSelectedDevice(null);
        }
    };

    const maxWidth = '450px';
    const ignoreMiniNavbar = true;
    const backgroundEnabled = false;

    return (
        <SoftBox sx={{ height: '100%', paddingBottom: '8px' }}>
            {sidebarWrapper((
                <>
                    {showMapOptions && (
                        <SoftBox mb={1} sx={({ palette: { white }, functions: { rgba } }) => ({ borderRadius: '16px', backgroundColor: rgba(white.main, 0.7), transition: 'height 200ms ease-in-out' })}>
                            <SoftBox p={1}>
                                <SoftBox>
                                    <SoftBox display="flex" alignItems="center">
                                        <SoftBox flex={1}>
                                            <Autocomplete
                                                id="map-option-select"
                                                selectOnFocus
                                                clearOnBlur
                                                handleHomeEndKeys
                                                options={mapOptions || []}
                                                groupBy={(option) => option?.category}
                                                getOptionLabel={(option) => option?.label || ""}
                                                value={mapOptions.find((opt) => opt.value === mapOption?.value) || null}
                                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                                onChange={(event, newValue) => {
                                                    setMapOption(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="outlined"
                                                        placeholder="Select a Filter..."
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                border: "none", // Optional: cleans up the input if you want it flush
                                                            }
                                                        }}
                                                    />
                                                )}
                                            />
                                        </SoftBox>
                                        {(canAssignmentsView && inspections.length > 0 && !isEditMode) && (
                                            <SoftButton
                                                variant={isAttentionMode ? "gradient" : "text"}
                                                color={isAttentionMode ? "warning" : "dark"}
                                                iconOnly
                                                onClick={handleAttentionToggle}
                                                sx={({ palette: { white }, borders: { borderRadius } }) => ({ borderRadius: borderRadius.md, backgroundColor: white.main, ml: 0.5, minWidth: "40px" })}
                                                title={isAttentionMode ? "Disable Actioning Devices" : "Enable Actioning Devices"}
                                            >
                                                {isAttentionMode ? <NotificationsOff /> : <NotificationsActive />}
                                            </SoftButton>
                                        )}
                                        {canAdvancedDevicesEdit && (
                                            <>
                                                {isEditMode && (
                                                    <SoftButton
                                                        variant={"gradient"}
                                                        color={"error"}
                                                        iconOnly
                                                        onClick={handleEditCancel}
                                                        sx={({ palette: { white }, borders: { borderRadius } }) => ({ borderRadius: borderRadius.md, backgroundColor: white.main, ml: 0.5, minWidth: "40px" })}
                                                        title={"Cancel Editing"}
                                                    >
                                                        <Cancel />
                                                    </SoftButton>
                                                )}
                                                <SoftButton
                                                    variant={isEditMode ? "gradient" : "text"}
                                                    color={isEditMode ? "success" : "dark"}
                                                    iconOnly
                                                    onClick={handleEditToggle}
                                                    sx={({ palette: { white }, borders: { borderRadius } }) => ({ borderRadius: borderRadius.md, backgroundColor: white.main, ml: 0.5, minWidth: "40px" })}
                                                    title={isEditMode ? "Save Locations" : "Edit Map Locations"}
                                                >
                                                    {isEditMode ? <Save /> : <Edit />}
                                                </SoftButton>
                                            </>
                                        )}
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    )}
                    {selectedDevice != null && (
                        <SoftBox height={showMapOptions ? (deviceLayout == "mobile" ? "calc(100svh - 9.75rem)" : "calc(100svh - 11.75rem)") : (deviceLayout == "mobile" ? "calc(100svh - 5.75rem)" : "calc(100svh - 7.75rem)")} p={1} sx={({ palette: { white }, functions: { rgba } }) => ({ borderRadius: '16px', overflow: 'hidden', backgroundColor: rgba(white.main, 0.7), transition: 'height 200ms ease-in-out' })}>
                            <Card sx={{ height: "100%", background: '#f9f9f9' }}>
                                <SoftBox py={2} px={2} sx={{ overflowY: 'auto' }}>
                                    <SoftBox
                                        mt={deviceLayout == "mobile" ? 3.5 : 1}
                                        mx={1}
                                        display="flex"
                                        flexDirection="row"
                                        flexWrap="wrap"
                                        alignItems="baseline"
                                        sx={{ gap: 0.5 }}
                                    >
                                        <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ color: "#333", mr: 0.5, flexShrink: 1, width: 'fit-content' }}>{header}</SoftTypography>
                                        {subheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{subheader}{triheader && ', '}</SoftTypography>}
                                        {triheader && <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="bold" sx={{ color: "#777", minWidth: "fit-content" }}>{triheader}</SoftTypography>}
                                        {deviceLayout == 'mobile' && (
                                            <>
                                                <SoftBox
                                                    sx={{
                                                        position: 'absolute',
                                                        right: '5px',
                                                        top: '5px',
                                                        verticalAlign: 'center',
                                                        alignItems: 'center',
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#ddd',
                                                        p: '5px',
                                                        borderRadius: '50px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={((e) => setSelectedDevice(null))}
                                                >
                                                    <Close fontSize="small" />
                                                </SoftBox>
                                                {accessUserLocation && (
                                                    <SoftBox
                                                        sx={{
                                                            position: 'absolute',
                                                            left: '5px',
                                                            top: '5px',
                                                            verticalAlign: 'center',
                                                            alignItems: 'center',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            backgroundColor: '#ddd',
                                                            borderRadius: '50px'
                                                        }}
                                                    >
                                                        <MapLinkButton locations={{ user_loc: currentLocation, dest_loc: selectedDevice.position }} backgroundColor={'#ddd'} dimensions={"30px"} />
                                                    </SoftBox>
                                                )}
                                            </>
                                        )}
                                    </SoftBox>
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <SoftBox py={selectedDevice?.inspection_record_id ? 1 : 2} px={selectedDevice?.inspection_record_id ? 0 : 1}>
                                        {selectedDevice?.inspection_record_id ? (
                                            <InspectionLiveviewCard device={selectedDevice} />
                                        ) : (
                                            <>
                                                {selectedDevice.hardware_type == 'bk-camera' ? (
                                                    <CameraLatest uuid={selectedDevice.uuid} />
                                                ) : (
                                                    <SoftBox sx={{ maxHeight: '450px', overflowX: 'hidden' }}>
                                                        <LiveView device={selectedDevice} isPreview={true} minHeight={"450px"} nexus_id={user.organisations.find((org) => org.organisation_id == selectedDevice.organisation_id).nexus_id || null} isPerspectiveLiveview={selectedDevice?.overlay_id && selectedDevice.overlay_id != 'no_frame' ? true : false} />
                                                    </SoftBox>
                                                )}
                                            </>
                                        )}
                                    </SoftBox>
                                    {deviceLayout == 'mobile' && (
                                        <>
                                            <SoftBox my={1}>
                                                <Divider />
                                            </SoftBox>
                                            {selectedDevice.inspection_record_id ? (
                                                <SoftBox display="flex" gap={0.5}>
                                                    <SoftButton component={Link} to={`../../operations/assignments/${selectedDevice.inspection_record_id}`} variant="contained" color="dark" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        Complete Assignment
                                                    </SoftButton>
                                                </SoftBox>
                                            ) : (
                                                <SoftBox display="flex" gap={0.5}>
                                                    <SoftButton component={Link} to="/operations/inspections/new" state={{ currentDevice: selectedDevice }} variant="contained" color="dark" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        New Inspection
                                                    </SoftButton>
                                                    <SoftButton component={Link} to={`../devices/${selectedDevice.uuid}`} variant="contained" color="light" size="small" fullWidth sx={{ padding: '0.5rem !important' }}>
                                                        View Device Details
                                                    </SoftButton>
                                                </SoftBox>
                                            )}
                                        </>
                                    )}
                                    <SoftBox my={1}>
                                        <Divider />
                                    </SoftBox>
                                    <Card>
                                        <SoftBox mx={1}>
                                            <HealthStatusCard statistics={selectedDevice} variant={deviceLayout == "mobile" ? "d6" : "d5"} showAdvancedDeviceLevel={showAdvancedDeviceLevel} transparent={true} />
                                            {!selectedDevice.removed && (
                                                <>
                                                    {(typeof selectedDevice?.battery != 'undefined' && typeof selectedDevice?.temperature != 'undefined') && (
                                                        <>
                                                            <SoftBox my={0.5}>
                                                                <Divider />
                                                            </SoftBox>
                                                            <Grid container>
                                                                <Grid item xxs={12} x4l={6}>
                                                                    <StatsCounterCard
                                                                        count={selectedDevice.battery}
                                                                        suffix="%"
                                                                        title="Battery"
                                                                        description=""
                                                                        transparent={true}
                                                                    />
                                                                </Grid>
                                                                <Box sx={{
                                                                    width: 0, py: 1, display: "flex", justifyContent: "center",
                                                                    opacity: 0,
                                                                    [theme.breakpoints.up("x4l")]: {
                                                                        opacity: 1,
                                                                    }
                                                                }}>
                                                                    <Divider orientation="vertical" flexItem />
                                                                </Box>
                                                                <Grid item xxs={12}
                                                                    sx={{
                                                                        [theme.breakpoints.up("x4l")]: {
                                                                            display: 'none',
                                                                        }
                                                                    }}>
                                                                    <Divider orientation="horizontal" />
                                                                </Grid>
                                                                <Grid item xxs={12} x4l={6}>
                                                                    <StatsCounterCard
                                                                        count={selectedDevice.temperature}
                                                                        suffix={<>&deg;C</>}
                                                                        title="Temperature"
                                                                        description=""
                                                                        transparent={true}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </>
                                                    )}
                                                    <SoftBox my={0.5}>
                                                        <Divider />
                                                    </SoftBox>
                                                    <DeviceStatsCard icon={getConnectivityIcon(selectedDevice, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getConnectivityIcon(selectedDevice, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                                    <SoftBox my={0.5}>
                                                        <Divider />
                                                    </SoftBox>
                                                    <DeviceStatsCard icon={getDeviceTypeIcon(selectedDevice, showAdvancedDeviceLevel)} transparent={true} variant="h6" title={{ text: getDeviceTypeIcon(selectedDevice, showAdvancedDeviceLevel).text, fontWeight: 'medium' }} />
                                                </>
                                            )}
                                        </SoftBox>
                                    </Card>
                                </SoftBox>
                            </Card>
                        </SoftBox>
                    )}
                </>
            ), controller, selectedDevice != null, maxWidth, ignoreMiniNavbar, backgroundEnabled)}
        </SoftBox>
    );
}

StopmonitorSidebar.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    selectedDevice: PropTypes.object,
    setSelectedDevice: PropTypes.func,
    mapOptions: PropTypes.array,
    inspections: PropTypes.array,
    mapOption: PropTypes.object,
    setMapOption: PropTypes.func,
    isAttentionMode: PropTypes.bool,
    setIsAttentionMode: PropTypes.func,
    isEditMode: PropTypes.bool,
    setIsEditMode: PropTypes.func,
    handleSaveLocations: PropTypes.func,
}

export default StopmonitorSidebar;